/*
 * Canonical founder / entity page for Konrad Kwiatkowski.
 *
 * SEO sprint scope only. No product/feature/logic changes. PL default with
 * EN via the language switcher — the layout reads i18n.language via
 * useTranslation and swaps title / body / label text between languages.
 *
 * Doctrine preserved verbatim:
 *   PL: "AI przygotowuje. Ludzie decydują. Każde działanie zostawia ślad audytowy."
 *   EN: "AI prepares. Humans decide. Every action leaves an audit trail."
 *
 * Guardrails: no personal/family/legal/medical/veteran details, no social
 * links unless explicitly supplied later, no political content, no
 * unverified claims.
 */
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import HussarTopNav from '../components/HussarTopNav';

const CANONICAL_URL = 'https://hussarai.org/konrad-kwiatkowski';
const FOUNDER_IMAGE_WEBP_PATH = '/images/konrad-kwiatkowski-founder-hussarai.webp';
const FOUNDER_IMAGE_JPG_PATH = '/images/konrad-kwiatkowski-founder-hussarai.jpg';
const FOUNDER_IMAGE_ABS_URL = 'https://hussarai.org/images/konrad-kwiatkowski-founder-hussarai.webp';

const setHeadTag = (selector, attr, value) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    const isMeta = selector.startsWith('meta');
    el = document.createElement(isMeta ? 'meta' : 'link');
    if (selector.includes('property=')) {
      const m = selector.match(/property="([^"]+)"/); if (m) el.setAttribute('property', m[1]);
    } else if (selector.includes('name=')) {
      const m = selector.match(/name="([^"]+)"/); if (m) el.setAttribute('name', m[1]);
    } else if (selector.includes('rel=')) {
      const m = selector.match(/rel="([^"]+)"/); if (m) el.setAttribute('rel', m[1]);
    }
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const setJsonLd = (id, payload) => {
  let el = document.head.querySelector(`script[data-jsonld-id="${id}"]`);
  if (!el) {
    el = document.createElement('script');
    el.setAttribute('type', 'application/ld+json');
    el.setAttribute('data-jsonld-id', id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(payload);
};

const styles = {
  main: {
    minHeight: '100vh',
    background: 'var(--hussar-bg-page, #0B1220)',
    color: 'var(--hussar-text-primary, #E5E7EB)',
    padding: '56px 20px 96px',
  },
  container: { maxWidth: 960, margin: '0 auto' },
  backLink: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    fontSize: 13, color: 'rgba(125,211,252,0.85)',
    textDecoration: 'none', marginBottom: 28,
  },
  heroGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) 260px',
    gap: 40,
    alignItems: 'start',
    marginBottom: 32,
  },
  heroText: { minWidth: 0 },
  heroPortrait: { justifySelf: 'end' },
  portraitImg: {
    display: 'block',
    width: '100%',
    maxWidth: 260,
    height: 'auto',
    aspectRatio: '3 / 4',
    objectFit: 'cover',
    borderRadius: 12,
    border: '1px solid rgba(148,163,184,0.18)',
    boxShadow: '0 12px 32px rgba(0,0,0,0.35)',
  },
  eyebrow: {
    fontSize: 12, letterSpacing: 2, textTransform: 'uppercase',
    color: 'rgba(125,211,252,0.7)', margin: '0 0 12px',
  },
  h1: {
    fontSize: 40, lineHeight: 1.1, margin: '0 0 8px',
    color: '#F1F5F9', fontWeight: 700, letterSpacing: '-0.5px',
  },
  role: {
    fontSize: 17, color: '#7DD3FC', fontWeight: 500,
    margin: '0 0 20px', letterSpacing: 0.2,
  },
  heroDoctrine: {
    fontSize: 15, fontStyle: 'italic', color: '#CBD5F5',
    margin: '0 0 8px',
  },
  h2: {
    fontSize: 20, margin: '32px 0 12px', color: '#F1F5F9', fontWeight: 600,
  },
  p: { fontSize: 16, lineHeight: 1.7, color: '#CBD5F5', margin: '0 0 16px' },
  list: { fontSize: 15, lineHeight: 1.75, color: '#CBD5F5', paddingLeft: 20, margin: '0 0 16px' },
  doctrine: {
    marginTop: 32, padding: '16px 18px',
    background: 'rgba(148,163,184,0.06)',
    borderLeft: '3px solid #7DD3FC',
    borderRadius: 6, fontStyle: 'italic',
    color: '#CBD5F5', fontSize: 15,
  },
  contactCard: {
    marginTop: 28, padding: '18px 20px',
    borderRadius: 10, border: '1px solid rgba(148,163,184,0.15)',
    background: 'rgba(15,23,42,0.5)',
  },
  a: { color: '#7DD3FC', textDecoration: 'underline' },
};

const KonradKwiatkowski = () => {
  const { i18n } = useTranslation();
  const isPl = (i18n?.language || 'pl').toLowerCase().startsWith('pl');

  const title = isPl
    ? 'Konrad Kwiatkowski — założyciel HussarAi'
    : 'Konrad Kwiatkowski | Founder of HussarAi';
  const description = isPl
    ? 'Konrad Kwiatkowski jest założycielem HussarAi — europejskiej platformy spójności akt imigracyjnych, opartej na nadzorze człowieka, weryfikacji dokumentów i audytowalnym AI.'
    : 'Konrad Kwiatkowski is the founder of HussarAi, a European immigration record-integrity platform built around human oversight, document consistency and auditable AI.';

  useEffect(() => {
    document.title = title;
    setHeadTag('meta[name="description"]', 'content', description);
    setHeadTag('meta[property="og:title"]', 'content', title);
    setHeadTag('meta[property="og:description"]', 'content', description);
    setHeadTag('meta[property="og:type"]', 'content', 'profile');
    setHeadTag('meta[property="og:url"]', 'content', CANONICAL_URL);
    setHeadTag('meta[property="og:site_name"]', 'content', 'HussarAi');
    setHeadTag('link[rel="canonical"]', 'href', CANONICAL_URL);
    // Canonical founder portrait — same file reused by Press Kit + Wiedza.
    setHeadTag('meta[property="og:image"]', 'content', FOUNDER_IMAGE_ABS_URL);
    setHeadTag('meta[property="og:image:width"]', 'content', '1152');
    setHeadTag('meta[property="og:image:height"]', 'content', '1536');
    setHeadTag('meta[property="og:image:alt"]', 'content', 'Konrad Kwiatkowski, Founder of HussarAi');
    setHeadTag('meta[name="twitter:card"]', 'content', 'summary_large_image');
    setHeadTag('meta[name="twitter:image"]', 'content', FOUNDER_IMAGE_ABS_URL);
    setJsonLd('konrad-kwiatkowski-profile', {
      '@context': 'https://schema.org',
      '@type': 'ProfilePage',
      '@id': 'https://hussarai.org/konrad-kwiatkowski#profile',
      url: CANONICAL_URL,
      name: 'Konrad Kwiatkowski | Founder of HussarAi',
      mainEntity: {
        '@type': 'Person',
        '@id': 'https://hussarai.org/konrad-kwiatkowski#person',
        name: 'Konrad Kwiatkowski',
        url: CANONICAL_URL,
        jobTitle: 'Founder of HussarAi',
        image: {
          '@type': 'ImageObject',
          url: FOUNDER_IMAGE_ABS_URL,
          contentUrl: FOUNDER_IMAGE_ABS_URL,
          caption: 'Konrad Kwiatkowski, Founder of HussarAi',
          width: 1152,
          height: 1536,
        },
        worksFor: {
          '@type': 'Organization',
          '@id': 'https://hussarai.org/#org',
          name: 'HussarAi',
          url: 'https://hussarai.org/',
        },
        sameAs: [],
      },
    });
  }, [title, description]);

  return (
    <main data-testid="konrad-kwiatkowski-page" style={styles.main}>
      <HussarTopNav />
      <div style={styles.container}>
        <Link to="/" style={styles.backLink} data-testid="founder-back-link">
          <ArrowLeft className="w-4 h-4" /> {isPl ? 'Powrót' : 'Back'}
        </Link>

        <p style={styles.eyebrow} data-testid="founder-eyebrow">
          {isPl ? 'HussarAi · Założyciel' : 'HussarAi · Founder'}
        </p>

        {/* Hero: name / role / doctrine on the left, canonical portrait on the right.
            Same image file (WebP with JPG fallback) is reused by Press Kit and Wiedza. */}
        <section style={styles.heroGrid} data-testid="founder-hero">
          <div style={styles.heroText}>
            <h1 style={styles.h1} data-testid="founder-h1">
              Konrad Kwiatkowski
            </h1>
            <p style={styles.role} data-testid="founder-role">
              {isPl ? 'Założyciel, HussarAi' : 'Founder, HussarAi'}
            </p>
            <p style={styles.heroDoctrine} data-testid="founder-hero-doctrine">
              <em>
                {isPl
                  ? 'AI przygotowuje. Ludzie decydują.'
                  : 'AI prepares. Humans decide.'}
              </em>
            </p>
          </div>
          <div style={styles.heroPortrait}>
            <picture>
              <source srcSet={FOUNDER_IMAGE_WEBP_PATH} type="image/webp" />
              <img
                src={FOUNDER_IMAGE_JPG_PATH}
                alt={isPl
                  ? 'Konrad Kwiatkowski, założyciel HussarAi'
                  : 'Konrad Kwiatkowski, founder of HussarAi'}
                title={isPl
                  ? 'Konrad Kwiatkowski — założyciel HussarAi'
                  : 'Konrad Kwiatkowski — Founder of HussarAi'}
                width={1152}
                height={1536}
                loading="eager"
                fetchPriority="high"
                style={styles.portraitImg}
                data-testid="founder-portrait"
              />
            </picture>
          </div>
        </section>
        {/* Stack columns on narrow viewports. Scoped to this page only. */}
        <style>{`
          @media (max-width: 640px) {
            [data-testid="founder-hero"] {
              grid-template-columns: 1fr !important;
              justify-items: start;
            }
            [data-testid="founder-hero"] > div:last-child {
              justify-self: start;
            }
            [data-testid="founder-portrait"] {
              max-width: 200px;
            }
          }
        `}</style>

        {isPl ? (
          <>
            <p style={styles.p} data-testid="founder-lede">
              Konrad Kwiatkowski jest założycielem HussarAi — europejskiej platformy
              technologicznej skoncentrowanej na spójności akt imigracyjnych,
              weryfikacji dokumentów i sztucznej inteligencji nadzorowanej przez
              człowieka.
            </p>
            <p style={styles.p}>
              W ramach HussarAi Kwiatkowski rozwija systemy pomagające osobom i
              instytucjom porządkować złożone akta imigracyjne, wskazywać
              niespójności i przygotowywać informacje do weryfikacji przez
              człowieka — bez przenoszenia konsekwentnych decyzji na systemy
              automatyczne.
            </p>
            <p style={styles.p}>
              Jego pracę porządkuje prosta zasada: <em>AI przygotowuje. Ludzie decydują.</em>
            </p>
          </>
        ) : (
          <>
            <p style={styles.p} data-testid="founder-lede">
              Konrad Kwiatkowski is the founder of HussarAi, a European technology
              platform focused on immigration record integrity, document
              consistency and human-supervised artificial intelligence.
            </p>
            <p style={styles.p}>
              Through HussarAi, Kwiatkowski is developing systems designed to help
              individuals and institutions organize complex immigration records,
              identify inconsistencies and prepare information for human review —
              without transferring consequential decisions to automated systems.
            </p>
            <p style={styles.p}>
              His work centers on a simple principle: <em>AI prepares. Humans decide.</em>
            </p>
          </>
        )}

        <h2 style={styles.h2} data-testid="founder-focus-heading">
          {isPl ? 'Obszary pracy założyciela' : 'Founder focus'}
        </h2>
        <ul style={styles.list} data-testid="founder-focus-list">
          {(isPl ? [
            'spójność akt imigracyjnych',
            'nadzór człowieka w procesach wspieranych przez AI',
            'weryfikacja dokumentów',
            'ślady audytowe',
            'europejskie ramy nadzoru nad AI',
            'gotowość instytucjonalna w Polsce i Europie',
          ] : [
            'immigration record integrity',
            'human oversight in AI-supported workflows',
            'document consistency',
            'audit trails',
            'European AI governance',
            'Polish and European institutional readiness',
          ]).map((item) => (<li key={item}>{item}</li>))}
        </ul>

        <div style={styles.doctrine} data-testid="founder-doctrine">
          {isPl
            ? 'AI przygotowuje. Ludzie decydują. Każde działanie zostawia ślad audytowy.'
            : 'AI prepares. Humans decide. Every action leaves an audit trail.'}
        </div>

        <div style={styles.contactCard} data-testid="founder-contact">
          <h2 style={{ ...styles.h2, marginTop: 0 }}>
            {isPl ? 'Kontakt i linki oficjalne' : 'Contact and official links'}
          </h2>
          <ul style={styles.list}>
            <li>HussarAi: <a href="https://hussarai.org" style={styles.a}>https://hussarai.org</a></li>
            <li>{isPl ? 'Materiały prasowe' : 'Press kit'}: <Link to="/press-kit" style={styles.a} data-testid="founder-link-press-kit">/press-kit</Link></li>
            <li>{isPl ? 'Baza wiedzy' : 'Knowledge section'}: <Link to="/wiedza" style={styles.a} data-testid="founder-link-wiedza">/wiedza</Link></li>
            <li>Email: <a href="mailto:konrad_kwiatkowski@yahoo.com" style={styles.a}>konrad_kwiatkowski@yahoo.com</a></li>
          </ul>
        </div>

        {/* Quiet entity-disambiguation note at the very bottom (2026-02-19).
            Approved shorter variant. Only location on this page where the
            confusable near-spellings may appear. */}
        <p
          data-testid="founder-disambiguation-note"
          style={{
            marginTop: 40, paddingTop: 16,
            borderTop: '1px solid rgba(148,163,184,0.1)',
            fontSize: 12, lineHeight: 1.6,
            color: 'rgba(148,163,184,0.6)',
          }}
        >
          {isPl
            ? 'HussarAi jest odrębną marką europejskiej platformy spójności akt imigracyjnych i nie jest powiązany z podobnie nazwanymi projektami.'
            : 'HussarAi is a distinct European immigration record-integrity platform and is not affiliated with similarly named projects.'}
        </p>
      </div>
    </main>
  );
};

export default KonradKwiatkowski;
